import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface CardRunCandidate {
  id: number;
  name: { en: string; uk: string };
  quote: { en: string; uk: string };
  imageUrl: string;
  thumbUrl: string;
  votesCount: number;
  isWinner: boolean;
}

export interface CardRun {
  id: number;
  date: string;
  kind: string;
  rarity: string;
  status: string;
  themeHint: string;
  hasStudio: boolean;
  candidates: CardRunCandidate[];
}

export interface StudioRunData {
  description: string;
  hint: string;
  name: { en: string; uk: string };
  quote: { en: string; uk: string };
  anchorCandidateId?: number;
  anchorImageUrl?: string;
  feedback?: string;
  progress?: string;
  error?: string;
}

export interface StudioRun {
  id: number;
  kind: string;
  rarity: string;
  status: string;
  studio: StudioRunData | null;
  candidates: CardRunCandidate[];
}

export interface StudioIdea {
  rarity: string;
  nameEn: string;
  nameUk: string;
  quoteEn: string;
  quoteUk: string;
  description: string;
  hint: string;
}

export interface QuoteSuggestion {
  en: string;
  uk: string;
}

export interface PublishOverrides {
  nameEn?: string;
  nameUk?: string;
  quoteEn?: string;
  quoteUk?: string;
}

export interface NewSeed {
  nameEn: string;
  nameUk: string;
  quoteEn: string;
  quoteUk: string;
  rarityHint?: string;
  themeHint?: string;
}

export interface AdminSeed {
  id: number;
  name: { en: string; uk: string };
  quote: { en: string; uk: string };
  rarityHint?: string;
  themeHint?: string;
  status: string;
  usedAt?: string;
}

export interface CalendarEntry {
  id: number;
  kind: string;
  periodKey: string;
  day: string; // ISO date (Django DateField), e.g. "2026-08-15" - not a number
}

export interface CalendarMonth {
  month: string; // 'YYYY-MM'
  planned: boolean;
  entries: CalendarEntry[];
}

export interface AdminSpawn {
  id: number;
  cardId: number;
  cardName: { en: string; uk: string };
  rarity: string;
  thumbUrl: string;
  lng: number;
  lat: number;
  isAdminSpawn: boolean;
  expiresAt?: string;
}

export interface AdminCard {
  id: number;
  name: { en: string; uk: string };
  rarity: string;
  thumbUrl: string;
  value: number;
  localOnly: boolean;
  hidden: boolean;
  noRandomDrop: boolean;
  releasedAt: string;
  rewardCount: number;
  mapDropCount: number;
}

export interface AdminReward {
  id: number;
  targetType: 'quest' | 'trip';
  targetId: number;
  targetName: string;
  cardId: number;
  cardName: { en: string; uk: string };
  rarity: string;
  thumbUrl: string;
  createdAt: string;
}

export interface RewardTarget {
  id: number;
  name: string;
  isPrivate?: boolean;
  createdAt?: string;
  type?: string;
  questsCount?: number;
  obelisksCount?: number;
}

export interface BattleStake {
  username: string;
  cardName: { en: string; uk: string };
  value: number;
}

export interface AdminBattle {
  id: number;
  status: string;
  cap: number;
  creatorUsername: string;
  createdAt: string;
  expiresAt: string;
  stakes: BattleStake[];
}

export interface AdminSubmission {
  id: number;
  imageUrl: string;
  name: { en: string; uk: string };
  quote: { en: string; uk: string };
  username: string;
  status: string;
  rejectReason: string | null;
  createdAt: string;
}

export interface SubmissionApproveFields {
  nameEn?: string;
  nameUk?: string;
  quoteEn?: string;
  quoteUk?: string;
}

export interface DropSettings {
  questChance: number;
  dailyQuestChance: number;
  placeChance: number;
  weightCommon: number;
  weightRare: number;
  weightLegendary: number;
  ignoreWeeklyCooldown: boolean;
  ignoreDailyCap: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CardsAdminService {
  private baseUrl = `${environment.API_URL}/cards/admin`;

  constructor(private http: HttpClient) {}

  // Runs
  getRuns$(): Observable<CardRun[]> {
    return this.http.get<any[]>(`${this.baseUrl}/runs`).pipe(map((rows) => rows.map((r) => this.transformRun(r))));
  }

  // Presence (not truthiness) decides an override on the backend - only send keys the admin
  // actually touched, so untouched fields stay whatever the candidate carries (same contract
  // as approveSubmission$).
  publishRun$(runId: number, candidateId: number, overrides?: PublishOverrides): Observable<{ cardId: number }> {
    const body: any = { candidate_id: candidateId };
    if (overrides?.nameEn !== undefined) {
      body.name_en = overrides.nameEn;
    }
    if (overrides?.nameUk !== undefined) {
      body.name_uk = overrides.nameUk;
    }
    if (overrides?.quoteEn !== undefined) {
      body.quote_en = overrides.quoteEn;
    }
    if (overrides?.quoteUk !== undefined) {
      body.quote_uk = overrides.quoteUk;
    }
    return this.http
      .post<any>(`${this.baseUrl}/runs/${runId}/publish`, body)
      .pipe(map((res) => ({ cardId: res.card_id })));
  }

  sendRunToVote$(runId: number, date: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/runs/${runId}/to-vote`, { date });
  }

  // Studio
  studioGenerate$(idea: StudioIdea): Observable<{ runId: number }> {
    const body = {
      rarity: idea.rarity,
      name_en: idea.nameEn,
      name_uk: idea.nameUk,
      quote_en: idea.quoteEn,
      quote_uk: idea.quoteUk,
      description: idea.description,
      hint: idea.hint
    };
    return this.http.post<any>(`${this.baseUrl}/studio/generate`, body).pipe(map((r) => ({ runId: r.run_id })));
  }

  studioIterate$(candidateId: number, feedback: string): Observable<{ runId: number }> {
    return this.http
      .post<any>(`${this.baseUrl}/studio/iterate`, { candidate_id: candidateId, feedback })
      .pipe(map((r) => ({ runId: r.run_id })));
  }

  getStudioRun$(runId: number): Observable<StudioRun> {
    return this.http.get<any>(`${this.baseUrl}/studio/runs/${runId}`).pipe(map((r) => this.transformStudioRun(r)));
  }

  studioStop$(runId: number): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/studio/runs/${runId}/stop`, {});
  }

  studioResume$(runId: number): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/studio/runs/${runId}/resume`, {});
  }

  suggestQuotes$(name: string, description: string): Observable<QuoteSuggestion[]> {
    return this.http
      .post<any>(`${this.baseUrl}/studio/suggest-quote`, { name, description })
      .pipe(map((r) => r.suggestions ?? []));
  }

  // Seeds
  getSeeds$(): Observable<AdminSeed[]> {
    return this.http.get<any[]>(`${this.baseUrl}/seeds`).pipe(map((rows) => rows.map((s) => this.transformSeed(s))));
  }

  createSeed$(seed: NewSeed): Observable<AdminSeed> {
    const body = {
      name_en: seed.nameEn,
      name_uk: seed.nameUk,
      quote_en: seed.quoteEn,
      quote_uk: seed.quoteUk,
      rarity_hint: seed.rarityHint,
      theme_hint: seed.themeHint
    };
    return this.http.post<any>(`${this.baseUrl}/seeds`, body).pipe(map((s) => this.transformSeed(s)));
  }

  deleteSeed$(id: number): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/seeds/${id}`);
  }

  // Calendar
  getCalendarMonth$(month: string): Observable<CalendarMonth> {
    return this.http.get<any>(`${this.baseUrl}/calendar`, { params: { month } }).pipe(
      map((r) => ({
        month: r.month,
        planned: !!r.planned,
        entries: (r.entries ?? []).map((c: any) => this.transformCalendarEntry(c))
      }))
    );
  }

  setCalendarDay$(kind: string, day: string): Observable<CalendarEntry> {
    return this.http.post<any>(`${this.baseUrl}/calendar`, { kind, day }).pipe(map((c) => this.transformCalendarEntry(c)));
  }

  deleteCalendar$(id: number): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/calendar/${id}`);
  }

  // Spawns
  getSpawns$(): Observable<AdminSpawn[]> {
    return this.http.get<any[]>(`${this.baseUrl}/spawns`).pipe(map((rows) => rows.map((s) => this.transformSpawn(s))));
  }

  createSpawn$(cardId: number, lng: number, lat: number, expiresDays?: number): Observable<AdminSpawn> {
    const body: any = { card_id: cardId, lng, lat };
    if (expiresDays !== undefined) {
      body.expires_days = expiresDays;
    }
    return this.http.post<any>(`${this.baseUrl}/spawns`, body).pipe(map((s) => this.transformSpawn(s)));
  }

  deleteSpawn$(id: number): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/spawns/${id}`);
  }

  // Cards
  // Deliberately sends no params: the backend then returns released cards only,
  // so the spawn and reward pickers cannot offer a card that has not gone live.
  getCards$(): Observable<AdminCard[]> {
    return this.http
      .get<any>(`${this.baseUrl}/cards`)
      .pipe(map((res) => (res.rows ?? []).map((c: any) => this.transformCard(c))));
  }

  getCardCatalog$(params: {
    q?: string;
    rarity?: string;
    limit: number;
    offset: number;
  }): Observable<{ total: number; rows: AdminCard[] }> {
    const httpParams: Record<string, string> = {
      include_unreleased: '1',
      limit: String(params.limit),
      offset: String(params.offset)
    };
    if (params.q) {
      httpParams['q'] = params.q;
    }
    if (params.rarity) {
      httpParams['rarity'] = params.rarity;
    }
    return this.http
      .get<any>(`${this.baseUrl}/cards`, { params: httpParams })
      .pipe(
        map((res) => ({
          total: res.total ?? 0,
          rows: (res.rows ?? []).map((c: any) => this.transformCard(c))
        }))
      );
  }

  // Presence semantics: only the keys present are sent, and only those change.
  updateCard$(
    id: number,
    changes: { hidden?: boolean; noRandomDrop?: boolean; localOnly?: boolean }
  ): Observable<AdminCard> {
    const body: any = {};
    if (changes.hidden !== undefined) {
      body.hidden = changes.hidden;
    }
    if (changes.noRandomDrop !== undefined) {
      body.no_random_drop = changes.noRandomDrop;
    }
    if (changes.localOnly !== undefined) {
      body.local_only = changes.localOnly;
    }
    return this.http.post<any>(`${this.baseUrl}/cards/${id}`, body).pipe(map((c) => this.transformCard(c)));
  }

  // Rewards
  getRewards$(): Observable<AdminReward[]> {
    return this.http
      .get<any[]>(`${this.baseUrl}/rewards`)
      .pipe(map((rows) => rows.map((r) => this.transformReward(r))));
  }

  pinReward$(targetType: 'quest' | 'trip', targetId: number, cardId: number): Observable<AdminReward> {
    return this.http
      .post<any>(`${this.baseUrl}/rewards`, { target_type: targetType, target_id: targetId, card_id: cardId })
      .pipe(map((r) => this.transformReward(r)));
  }

  deleteReward$(id: number): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/rewards/${id}`);
  }

  searchRewardTargets$(type: 'quest' | 'trip', q: string): Observable<RewardTarget[]> {
    return this.http.get<any[]>(`${this.baseUrl}/reward-targets`, { params: { type, q } }).pipe(
      map((rows) =>
        rows.map((r) => ({
          id: r.id,
          name: r.name,
          isPrivate: r.is_private,
          createdAt: r.created_at,
          type: r.type,
          questsCount: r.quests_count,
          obelisksCount: r.obelisks_count
        }))
      )
    );
  }

  // Battles
  getBattles$(): Observable<AdminBattle[]> {
    return this.http.get<any[]>(`${this.baseUrl}/battles`).pipe(map((rows) => rows.map((b) => this.transformBattle(b))));
  }

  forceCancelBattle$(id: number): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/battles/${id}/force-cancel`, {});
  }

  // Submissions
  getSubmissions$(status: 'pending' | 'all'): Observable<AdminSubmission[]> {
    return this.http
      .get<any[]>(`${this.baseUrl}/submissions`, { params: { status } })
      .pipe(map((rows) => rows.map((s) => this.transformSubmission(s))));
  }

  // Presence (not truthiness) decides an override on the backend - only send keys the admin
  // actually touched, so untouched fields stay whatever the submitter originally wrote.
  approveSubmission$(id: number, fields: SubmissionApproveFields): Observable<unknown> {
    const body: any = {};
    if (fields.nameEn !== undefined) {
      body.name_en = fields.nameEn;
    }
    if (fields.nameUk !== undefined) {
      body.name_uk = fields.nameUk;
    }
    if (fields.quoteEn !== undefined) {
      body.quote_en = fields.quoteEn;
    }
    if (fields.quoteUk !== undefined) {
      body.quote_uk = fields.quoteUk;
    }
    return this.http.post(`${this.baseUrl}/submissions/${id}/approve`, body);
  }

  rejectSubmission$(id: number, reason: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/submissions/${id}/reject`, { reason });
  }

  // Drop settings
  getDropSettings$(): Observable<DropSettings> {
    return this.http.get<any>(`${this.baseUrl}/drop-settings`).pipe(map((s) => this.transformDropSettings(s)));
  }

  saveDropSettings$(s: DropSettings): Observable<DropSettings> {
    const body = {
      quest_chance: s.questChance,
      daily_quest_chance: s.dailyQuestChance,
      place_chance: s.placeChance,
      weight_common: s.weightCommon,
      weight_rare: s.weightRare,
      weight_legendary: s.weightLegendary,
      ignore_weekly_cooldown: s.ignoreWeeklyCooldown,
      ignore_daily_cap: s.ignoreDailyCap
    };
    return this.http.post<any>(`${this.baseUrl}/drop-settings`, body).pipe(map((r) => this.transformDropSettings(r)));
  }

  // Data transformation
  private transformRun(r: any): CardRun {
    return {
      id: r.id,
      date: r.date,
      kind: r.kind,
      rarity: r.rarity,
      status: r.status,
      themeHint: r.theme_hint,
      hasStudio: !!r.has_studio,
      candidates: (r.candidates ?? []).map((c: any) => this.transformCandidate(c))
    };
  }

  private transformStudioRun(r: any): StudioRun {
    const s = r.data?.studio;
    return {
      id: r.id,
      kind: r.kind,
      rarity: r.rarity,
      status: r.status,
      studio: s
        ? {
            description: s.description ?? '',
            hint: s.hint ?? '',
            name: s.name ?? { en: '', uk: '' },
            quote: s.quote ?? { en: '', uk: '' },
            anchorCandidateId: s.anchor_candidate_id,
            anchorImageUrl: s.anchor_image_url,
            feedback: s.feedback,
            progress: s.progress,
            error: s.error
          }
        : null,
      candidates: (r.candidates ?? []).map((c: any) => this.transformCandidate(c))
    };
  }

  private transformCandidate(c: any): CardRunCandidate {
    return {
      id: c.id,
      name: c.name,
      quote: c.quote,
      imageUrl: c.image_url,
      thumbUrl: c.thumb_url,
      votesCount: c.votes_count,
      isWinner: c.is_winner
    };
  }

  private transformSeed(s: any): AdminSeed {
    return {
      id: s.id,
      name: s.name,
      quote: s.quote,
      rarityHint: s.rarity_hint,
      themeHint: s.theme_hint,
      status: s.status,
      usedAt: s.used_at
    };
  }

  private transformCalendarEntry(c: any): CalendarEntry {
    return {
      id: c.id,
      kind: c.kind,
      periodKey: c.period_key,
      day: c.day
    };
  }

  private transformSpawn(s: any): AdminSpawn {
    return {
      id: s.id,
      cardId: s.card_id,
      cardName: s.card_name,
      rarity: s.rarity,
      thumbUrl: s.thumb_url,
      lng: s.lng,
      lat: s.lat,
      isAdminSpawn: s.is_admin_spawn,
      expiresAt: s.expires_at
    };
  }

  private transformCard(c: any): AdminCard {
    return {
      id: c.id,
      name: c.name,
      rarity: c.rarity,
      thumbUrl: c.thumb_url,
      value: c.value,
      localOnly: !!c.local_only,
      hidden: !!c.hidden,
      noRandomDrop: !!c.no_random_drop,
      releasedAt: c.released_at,
      rewardCount: c.reward_count ?? 0,
      mapDropCount: c.map_drop_count ?? 0
    };
  }

  private transformReward(r: any): AdminReward {
    return {
      id: r.id,
      targetType: r.target_type,
      targetId: r.target_id,
      targetName: r.target_name,
      cardId: r.card_id,
      cardName: r.card_name,
      rarity: r.rarity,
      thumbUrl: r.thumb_url,
      createdAt: r.created_at
    };
  }

  private transformBattle(b: any): AdminBattle {
    return {
      id: b.id,
      status: b.status,
      cap: b.cap,
      creatorUsername: b.creator_username,
      createdAt: b.created_at,
      expiresAt: b.expires_at,
      stakes: (b.stakes ?? []).map((s: any) => ({
        username: s.username,
        cardName: s.card_name,
        value: s.value
      }))
    };
  }

  private transformSubmission(s: any): AdminSubmission {
    return {
      id: s.id,
      imageUrl: s.image_url,
      name: s.name,
      quote: s.quote,
      username: s.username,
      status: s.status,
      rejectReason: s.reject_reason,
      createdAt: s.created_at
    };
  }

  private transformDropSettings(s: any): DropSettings {
    return {
      questChance: s.quest_chance,
      dailyQuestChance: s.daily_quest_chance,
      placeChance: s.place_chance,
      weightCommon: s.weight_common,
      weightRare: s.weight_rare,
      weightLegendary: s.weight_legendary,
      ignoreWeeklyCooldown: !!s.ignore_weekly_cooldown,
      ignoreDailyCap: !!s.ignore_daily_cap
    };
  }
}
